import { createConsumer } from '@rails/actioncable';

/**
 * OperatorCableManager - Operator 專用的 Action Cable 單例管理器
 *
 * 功能：
 * - 管理 Operator 的 WebSocket 連接
 * - 防止 fd 洩漏和 port exhaustion
 * - 自動重連機制
 * - 訂閱管理和清理
 *
 * 使用方式：
 * ```javascript
 * import operatorCable from '@/lib/operatorCable';
 *
 * useEffect(() => {
 *   const unsubscribe = operatorCable.subscribe('NotificationChannel', {
 *     // params 會自動從 current_operator 取得
 *   }, {
 *     received: (data) => console.log(data)
 *   });
 *
 *   return unsubscribe; // ✅ 務必在 cleanup 中呼叫
 * }, []);
 * ```
 */
class OperatorCableManager {
  constructor() {
    this.cable = null;
    this.subscriptions = new Map(); // Map<subscriptionId, {subscription, channelName, params}>
    this.sharedSubscriptions = new Map(); // Map<channelKey, {subscription, refCount, subscriptionIds}>
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10; // 🔥 最大重連次數
    this.isReconnecting = false;
    this.visibilityListenerAdded = false; // 🔥 防止重複添加 listener
  }

  connect() {
    if (!this.cable) {
      // ✅ 從 meta tag 讀取 cable URL（由 Rails action_cable_meta_tag 設定）
      const baseUrl = document.querySelector('meta[name="action-cable-url"]')?.content || '/cable';
      const url = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}role=operator`;
      this.cable = createConsumer(url);
      this._setupConnectionListeners();
      this._setupVisibilityListener(); // 🔥 監聯 tab 切換
      console.log(`[OperatorCable] WebSocket connection created to ${url}`);
    }
    return this.cable;
  }

  subscribe(channelName, params = {}, callbacks = {}) {
    const cable = this.connect();

    // ✅ 使用 Operators 專用的 Channel（帶 namespace）
    const fullChannelName = channelName.includes('::') ? channelName : `Operators::${channelName}`;

    // 🔥 建立唯一的 channel key（用於識別相同的訂閱）
    const channelKey = JSON.stringify({ channel: fullChannelName, ...params });

    // 🔥 檢查是否已經有相同的訂閱
    let shared = this.sharedSubscriptions.get(channelKey);

    if (!shared) {
      // 🔥 第一次訂閱，建立真正的 WebSocket 訂閱
      const subscription = cable.subscriptions.create(
        { channel: fullChannelName, ...params },
        {
          received: (data) => {
            console.log(`[OperatorCable] Received from ${channelName}:`, data);
            // 🔥 通知所有使用此訂閱的 callbacks
            const shared = this.sharedSubscriptions.get(channelKey);
            if (shared) {
              shared.subscriptionIds.forEach((id) => {
                const sub = this.subscriptions.get(id);
                if (sub?.callbacks?.received) {
                  sub.callbacks.received(data);
                }
              });
            }
          },
          connected: () => {
            console.log(`[OperatorCable] Connected to ${channelName}`);
            const shared = this.sharedSubscriptions.get(channelKey);
            if (shared) {
              shared.subscriptionIds.forEach((id) => {
                const sub = this.subscriptions.get(id);
                if (sub?.callbacks?.connected) {
                  sub.callbacks.connected();
                }
              });
            }
          },
          disconnected: () => {
            console.log(`[OperatorCable] Disconnected from ${channelName}`);
            const shared = this.sharedSubscriptions.get(channelKey);
            if (shared) {
              shared.subscriptionIds.forEach((id) => {
                const sub = this.subscriptions.get(id);
                if (sub?.callbacks?.disconnected) {
                  sub.callbacks.disconnected();
                }
              });
            }
          },
          rejected: () => {
            console.error(`[OperatorCable] Subscription rejected: ${channelName}`);
            const shared = this.sharedSubscriptions.get(channelKey);
            if (shared) {
              shared.subscriptionIds.forEach((id) => {
                const sub = this.subscriptions.get(id);
                if (sub?.callbacks?.rejected) {
                  sub.callbacks.rejected();
                }
              });
            }
          }
        }
      );

      shared = {
        subscription,
        refCount: 0,
        subscriptionIds: new Set(),
        channelName,
        params
      };
      this.sharedSubscriptions.set(channelKey, shared);
      console.log(`[OperatorCable] 🆕 Created shared subscription for ${channelName}`);
    }

    // 🔥 增加引用計數
    shared.refCount++;
    const subscriptionId = `${channelName}_${Date.now()}_${Math.random()}`;
    shared.subscriptionIds.add(subscriptionId);

    // 儲存此訂閱的資訊（包含 callbacks）
    this.subscriptions.set(subscriptionId, {
      channelKey,
      channelName,
      params,
      callbacks
    });

    console.log(`[OperatorCable] ✅ Subscribed to ${channelName} (refCount: ${shared.refCount}, total subs: ${this.subscriptions.size})`);

    // 返回取消訂閱函數
    return () => this._unsubscribe(subscriptionId);
  }

  _unsubscribe(subscriptionId) {
    const sub = this.subscriptions.get(subscriptionId);
    if (!sub) {
      return;
    }

    const shared = this.sharedSubscriptions.get(sub.channelKey);
    if (!shared) {
      this.subscriptions.delete(subscriptionId);
      return;
    }

    // 減少引用計數
    shared.refCount--;
    shared.subscriptionIds.delete(subscriptionId);
    this.subscriptions.delete(subscriptionId);

    // 如果沒有人使用此訂閱了，真正取消 WebSocket 訂閱
    if (shared.refCount === 0) {
      shared.subscription.unsubscribe();
      this.sharedSubscriptions.delete(sub.channelKey);
    }
  }

  _disconnect() {
    if (this.cable) {
      this.cable.disconnect();
      this.cable = null;
      console.log('[OperatorCable] WebSocket disconnected (no active subscriptions)');
    }
  }

  _setupConnectionListeners() {
    if (!this.cable?.connection) return;

    // 🔥 監聽連接成功事件
    const originalOpen = this.cable.connection.events.open;
    this.cable.connection.events.open = () => {
      this.reconnectAttempts = 0;
      this.isReconnecting = false;
      console.log('[OperatorCable] ✅ WebSocket connected');
      if (originalOpen) originalOpen.call(this.cable.connection);
    };

    // 🔥 監聽連接關閉事件
    const originalClose = this.cable.connection.events.close;
    this.cable.connection.events.close = (event) => {
      this.isReconnecting = false; // 🔥 Reset reconnecting state
      console.warn('[OperatorCable] ⚠️ WebSocket closed', event);
      // ActionCable 會自動重連，我們只記錄日誌
      if (originalClose) originalClose.call(this.cable.connection, event);
    };

    // 🔥 監聽連接錯誤事件
    const originalError = this.cable.connection.events.error;
    this.cable.connection.events.error = (event) => {
      this.isReconnecting = false; // 🔥 Reset reconnecting state
      this.reconnectAttempts++;
      console.error(`[OperatorCable] ❌ Connection error (attempt ${this.reconnectAttempts})`);

      if (this.reconnectAttempts > this.maxReconnectAttempts) {
        console.error('[OperatorCable] 🚫 Max reconnect attempts reached');
      }

      if (originalError) originalError.call(this.cable.connection, event);
    };
  }

  // 檢查底層 WebSocket 是否真正存活
  // ActionCable 的 isActive() 只看內部狀態機，不檢查實際 WebSocket readyState
  // 瀏覽器在背景可能靜默殺掉 WebSocket 而不觸發 close event（zombie connection）
  _isWebSocketAlive() {
    return this.isWebSocketAlive();
  }

  /**
   * 公開方法：檢查 WebSocket 是否真正存活
   * @returns {boolean} true = WebSocket 處於 OPEN 狀態
   */
  isWebSocketAlive() {
    const ws = this.cable?.connection?.webSocket;
    // WebSocket.OPEN === 1
    return ws && ws.readyState === WebSocket.OPEN;
  }

  // 🔥 監聽瀏覽器 tab 切換 (visibilitychange)
  // 當用戶切回 tab 時，檢查連接狀態並嘗試重連
  _setupVisibilityListener() {
    if (this.visibilityListenerAdded) return;

    const handleVisibilityChange = () => {
      if (document.hidden) return;

      // 用戶切回 tab，檢查連接狀態
      console.log('[OperatorCable] Tab became visible, checking connection...');

      if (!this.cable?.connection) return;

      const isActive = this.cable.connection.isActive();
      const wsAlive = this._isWebSocketAlive();
      console.log('[OperatorCable] Connection state:', { isActive, wsAlive });

      if (isActive && wsAlive) {
        // 連線正常，不需處理
        return;
      }

      if (this.isReconnecting) {
        console.log('[OperatorCable] Already reconnecting, skip');
        return;
      }

      if (isActive && !wsAlive) {
        // Zombie connection: ActionCable 以為連線正常，但 WebSocket 已死
        // 必須先 disconnect 再 reopen，否則 ActionCable 不會重建 WebSocket
        console.warn('[OperatorCable] Zombie connection detected, forcing reconnect...');
        this.isReconnecting = true;
        this.cable.connection.monitor.stop();
        this.cable.connection.close();
        // 讓 close 事件處理完畢後再 reopen
        setTimeout(() => {
          if (this.cable?.connection) {
            this.cable.connection.monitor.start();
            this.cable.connection.open();
          }
          this.isReconnecting = false;
        }, 100);
      } else {
        // 正常斷線，嘗試 reopen
        console.log('[OperatorCable] Connection lost, reopening...');
        this.isReconnecting = true;
        this.cable.connection.reopen();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    this.visibilityListenerAdded = true;
    console.log('[OperatorCable] Visibility listener added');
  }

  getSubscriptionCount() {
    return this.subscriptions.size;
  }

  isConnected() {
    return this.cable !== null;
  }

  forceDisconnect() {
    console.warn('[OperatorCable] Force disconnecting all subscriptions');

    // 🔥 清理所有共享訂閱
    this.sharedSubscriptions.forEach((shared) => {
      shared.subscription.unsubscribe();
    });
    this.sharedSubscriptions.clear();
    this.subscriptions.clear();
    this._disconnect();
  }
}

const operatorCable = new OperatorCableManager();

if (import.meta.env.DEV) {
  window.operatorCable = operatorCable;
}

export default operatorCable;
